import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { EnergySectionProps } from './types'
import {
  InfoSection,
  PropertySectionHeader as SectionHeader,
  PropertySectionTitle as SectionTitle,
  SectionIcon,
  SectionContent,
  PropertyDataGrid as DataGrid,
  PropertyDataRow as DataRow,
  DataValue,
  DataValueWithUnit,
  DataUnit,
  PropertyStatusBadge as StatusBadge,
} from '../../../molecules/PropertyPanelStyles'
import { DataLabelWithTooltip } from '../../../molecules/DataLabelWithTooltip'
import { Icon } from '../../../atoms/Icon'
import { formatNumberSwiss } from '../../../../utils/number/formatUtils'
import { translateGwrValue } from '../../../../utils/gwrTranslations'

const DetailsLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: ${({ theme }) => theme.spacing.default};
  font-size: ${({ theme }) => theme.typography.fontSize.label};
  color: ${({ theme }) => theme.colors.text.tertiary};
  text-decoration: none;
  transition: color ${({ theme }) => theme.transitions.fast};

  svg {
    width: 12px;
    height: 12px;
    transition: transform ${({ theme }) => theme.transitions.fast};
  }

  &:hover {
    color: ${({ theme }) => theme.colors.primary};

    svg {
      transform: translateX(2px);
    }
  }
`

export const EnergySection: React.FC<EnergySectionProps> = ({
  building,
  detailsLink,
}) => {
  const { t } = useTranslation('explore')
  const { t: tCommon } = useTranslation('common')

  const getEnergyEfficiencyColor = (
    value: string | number | undefined
  ): string | undefined => {
    if (!value) return undefined
    const numValue = typeof value === 'string' ? parseFloat(value) : value
    if (isNaN(numValue)) return undefined

    if (numValue <= 50) return 'var(--success)'
    if (numValue <= 100) return 'var(--warning)'
    return 'var(--danger)'
  }

  const getSolarPotentialText = (exposure: number | undefined): string => {
    if (!exposure) return '-'
    if (exposure >= 1400) return t('common.solarPotential.excellent')
    if (exposure >= 1200) return t('common.solarPotential.veryGood')
    if (exposure >= 1000) return t('common.solarPotential.good')
    if (exposure >= 800) return t('common.solarPotential.moderate')
    return t('common.solarPotential.poor')
  }

  const getSolarPotentialColor = (exposure: number | undefined) => {
    if (!exposure) return undefined
    if (exposure >= 1400) return '#A80200'
    if (exposure >= 1200) return '#FA5502'
    if (exposure >= 1000) return '#FCAA00'
    if (exposure >= 800) return '#FFFC09'
    return '#2DC5FF'
  }

  const hasEnergyInfo =
    building?.minergie ||
    building?.power_source_heater ||
    building?.power_source_water_heater ||
    building?.heater_generator ||
    building?.heat_consumption_index ||
    building?.solar_average_exposure ||
    building?.solar_installed_power ||
    building?.energy_reference_area

  if (!hasEnergyInfo) {
    return null
  }

  return (
    <InfoSection>
      <SectionHeader>
        <SectionTitle>
          <SectionIcon>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
          </SectionIcon>
          {t('propertyTooltip.energy.title')}
        </SectionTitle>
      </SectionHeader>
      <SectionContent>
        <DataGrid>
          {building?.minergie && (
            <DataRow>
              <DataLabelWithTooltip
                label={t('propertyTooltip.energy.minergie')}
                explanation={tCommon('propertyExplanations.energy.minergie')}
              >
                {t('propertyTooltip.energy.minergie')}
              </DataLabelWithTooltip>
              <StatusBadge $status="active">{building.minergie}</StatusBadge>
            </DataRow>
          )}

          {building?.power_source_heater && (
            <DataRow>
              <DataLabelWithTooltip
                label={t('common.buildingTooltip.Source Energie du chauffage')}
                explanation={tCommon(
                  'propertyExplanations.energy.heatingEnergySource'
                )}
              >
                {t('common.buildingTooltip.Source Energie du chauffage')}
              </DataLabelWithTooltip>
              <DataValue>
                {translateGwrValue(
                  building.power_source_heater,
                  'energySource',
                  t
                )}
              </DataValue>
            </DataRow>
          )}

          {building?.power_source_water_heater && (
            <DataRow>
              <DataLabelWithTooltip
                label={t('common.buildingTooltip.Source Energie eau chaude')}
                explanation={tCommon(
                  'propertyExplanations.energy.waterHeatingSource'
                )}
              >
                {t('common.buildingTooltip.Source Energie eau chaude')}
              </DataLabelWithTooltip>
              <DataValue>
                {translateGwrValue(
                  building.power_source_water_heater,
                  'energySource',
                  t
                )}
              </DataValue>
            </DataRow>
          )}

          {building?.heater_generator && (
            <DataRow>
              <DataLabelWithTooltip
                label={t('common.buildingTooltip.Generateur chauffage')}
                explanation={tCommon(
                  'propertyExplanations.energy.heatingGenerator'
                )}
              >
                {t('common.buildingTooltip.Generateur chauffage')}
              </DataLabelWithTooltip>
              <DataValue>
                {translateGwrValue(
                  building.heater_generator,
                  'heaterGenerator',
                  t
                )}
              </DataValue>
            </DataRow>
          )}

          {building?.energy_reference_area && (
            <DataRow>
              <DataLabelWithTooltip
                label={t('common.info.Surface reference energetique')}
                explanation={tCommon(
                  'propertyExplanations.energy.energyReferenceArea'
                )}
              >
                {t('common.info.Surface reference energetique')}
              </DataLabelWithTooltip>
              <DataValueWithUnit>
                <DataValue>
                  {formatNumberSwiss(building.energy_reference_area)}
                </DataValue>
                <DataUnit>m²</DataUnit>
              </DataValueWithUnit>
            </DataRow>
          )}

          {building?.heat_consumption_index && (
            <DataRow>
              <DataLabelWithTooltip
                label={t('propertyTooltip.energy.idc')}
                explanation={tCommon('propertyExplanations.energy.idc')}
              >
                {t('propertyTooltip.energy.idc')}
              </DataLabelWithTooltip>
              <DataValueWithUnit>
                <DataValue
                  $color={getEnergyEfficiencyColor(
                    building.heat_consumption_index
                  )}
                >
                  {formatNumberSwiss(building.heat_consumption_index)}
                </DataValue>
                <DataUnit>MJ/m²·{t('common.year')}</DataUnit>
              </DataValueWithUnit>
            </DataRow>
          )}

          {building?.solar_average_exposure && (
            <DataRow>
              <DataLabelWithTooltip
                label={t('common.building.sunPotentiel')}
                explanation={tCommon(
                  'propertyExplanations.energy.solarPotential'
                )}
              >
                {t('common.building.sunPotentiel')}
              </DataLabelWithTooltip>
              <DataValue
                $color={getSolarPotentialColor(building.solar_average_exposure)}
              >
                {getSolarPotentialText(building.solar_average_exposure)}
              </DataValue>
            </DataRow>
          )}

          {building?.solar_installed_power && (
            <DataRow>
              <DataLabelWithTooltip
                label={t(
                  'common.buildingTooltip.Puissance photovoltaïque installée'
                )}
                explanation={tCommon(
                  'propertyExplanations.energy.solarInstalledPower'
                )}
              >
                {t('common.buildingTooltip.Puissance photovoltaïque installée')}
              </DataLabelWithTooltip>
              <DataValueWithUnit>
                <DataValue>
                  {formatNumberSwiss(building.solar_installed_power)}
                </DataValue>
                <DataUnit>kW</DataUnit>
              </DataValueWithUnit>
            </DataRow>
          )}

          {building?.solar_installed_date && (
            <DataRow>
              <DataLabelWithTooltip
                label={t('common.buildingTooltip.Date installation solaire')}
                explanation={tCommon(
                  'propertyExplanations.energy.solarInstallationDate'
                )}
              >
                {t('common.buildingTooltip.Date installation solaire')}
              </DataLabelWithTooltip>
              <DataValue>{building.solar_installed_date}</DataValue>
            </DataRow>
          )}

          {building?.solar_area && (
            <DataRow>
              <DataLabelWithTooltip
                label={t('common.buildingTooltip.Surface solaire')}
                explanation={tCommon('propertyExplanations.energy.solarArea')}
              >
                {t('common.buildingTooltip.Surface solaire')}
              </DataLabelWithTooltip>
              <DataValueWithUnit>
                <DataValue>{formatNumberSwiss(building.solar_area)}</DataValue>
                <DataUnit>m²</DataUnit>
              </DataValueWithUnit>
            </DataRow>
          )}
        </DataGrid>
        {detailsLink && (
          <DetailsLink href={detailsLink}>
            <Icon name="arrow-right" size="small" />
            {t('propertyTooltip.energy.viewDetails')}
          </DetailsLink>
        )}
      </SectionContent>
    </InfoSection>
  )
}

export default EnergySection
